import { useCallback, useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { FileDown } from "lucide-react";
import { Button } from "../components/atoms/Button";
import { MyRFPsTable } from "../components/organisms/MyRFPsTable";
import { UploadRfpDrawer, type UploadRfpFormData } from "../components/organisms/UploadRfpDrawer";
import { useRfps } from "../hooks/useRfps";
import { useRfpUsers } from "../hooks/useRfpUsers";
import { uploadRfp } from "../services/rfpsService";
import type { RfpItem, RfpSortBy, RfpSortDir, RfpStatus } from "../types/rfp";

export const RFPsUserListingPage = () => {
  const navigate = useNavigate();
  const {
    rfps,
    loading,
    error,
    page,
    pageSize,
    totalRows,
    searchInput,
    status,
    sortBy,
    sortDir,
    setSearchInput,
    setStatus,
    setPage,
    setPageSize,
    setSorting,
    reload,
  } = useRfps();
  const { users: ownerUsers, loading: ownersLoading, error: ownersError } = useRfpUsers();
  const [showFilter, setShowFilter] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    [setSearchInput],
  );

  const clearFilters = useCallback(() => {
    setStatus("");
  }, [setStatus]);

  const hasActiveFilters = status !== "";

  const handleUpload = useCallback(
    async (formData: UploadRfpFormData) => {
      setUploadError(null);
      try {
        await uploadRfp(formData);
        setIsDrawerOpen(false);
        await reload();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to upload RFP. Please try again.";
        setUploadError(message);
        throw err;
      }
    },
    [reload],
  );

  const handleSortChange = useCallback(
    (nextSortBy: RfpSortBy, nextSortDir: RfpSortDir) => {
      setSorting(nextSortBy, nextSortDir);
    },
    [setSorting],
  );

  const handleActionClick = useCallback(
    (rfp: RfpItem) => {
      navigate(`/my-rfps/rfps-question-answers/${encodeURIComponent(rfp.id)}`);
    },
    [navigate],
  );

  const handleDownloadUserGuide = useCallback(() => {
    const fileName = "IntelliDocSuite-RFP-User-Guide.pdf";
    const link = document.createElement("a");
    link.href = `/assets/${fileName}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  return (
    <section className="page-section">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-heading">My RFPs</h1>
          <p className="mt-1 text-sm text-gray-500">Manage and respond to RFP requests</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleDownloadUserGuide} className="shrink-0">
            <FileDown className="-ml-0.5 mr-1.5 h-4 w-4" />
            User Guide
          </Button>
          <Button
            onClick={() => {
              setUploadError(null);
              setIsDrawerOpen(true);
            }}
            className="shrink-0"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-0.5 mr-1.5 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
              />
            </svg>
            Upload RFP
          </Button>
        </div>
      </div>

      <div className="page-toolbar">
        <div className="relative max-w-sm flex-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search RFPs..."
            value={searchInput}
            onChange={handleSearchChange}
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowFilter((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filter
            {hasActiveFilters && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary-500 text-[10px] font-bold text-white">
                !
              </span>
            )}
          </button>

          {showFilter && (
            <div className="absolute left-0 top-full z-20 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Filters</p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-xs font-medium text-primary-500 hover:text-primary-600"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label
                    htmlFor="rfp-status-filter"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  >
                    Status
                  </label>
                  <select
                    id="rfp-status-filter"
                    value={status}
                    onChange={(e) => setStatus((e.target.value as RfpStatus | "") ?? "")}
                    className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                  >
                    <option value="">All</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="pending_review">Pending Review</option>
                    <option value="in_review">In Review</option>
                    <option value="completed">Completed</option>
                    <option value="error">Error</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
          <button
            type="button"
            onClick={reload}
            className="ml-3 font-medium underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      )}

      {uploadError && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {uploadError}
        </div>
      )}

      <MyRFPsTable
        rfps={rfps}
        isLoading={loading}
        totalRows={totalRows}
        page={page}
        perPage={pageSize}
        sortBy={sortBy}
        sortDir={sortDir}
        onChangePage={setPage}
        onChangeRowsPerPage={setPageSize}
        onSortChange={handleSortChange}
        onActionClick={handleActionClick}
      />

      <UploadRfpDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setUploadError(null);
          setIsDrawerOpen(false);
        }}
        onUpload={handleUpload}
        owners={ownerUsers}
        ownersLoading={ownersLoading}
        ownersError={ownersError}
      />
    </section>
  );
};
